'use client';

import type { StatsBlock, StatItem } from '@impact/types';
import { cn } from '@/lib/utils';
import { StatsOversizedNumbers } from './stats-variants/OversizedNumbers';

interface StatsBlockRendererProps {
  block: StatsBlock;
  editing?: boolean;
}

const GRID_CLASSES: Record<2 | 3 | 4, string> = {
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-2 lg:grid-cols-4',
};

const ALIGN_CLASS = {
  left: 'text-left items-start',
  center: 'text-center items-center',
  right: 'text-right items-end',
} as const;

const ACCENT_CLASS = {
  primary: 'text-primary',
  foreground: 'text-foreground',
  muted: 'text-muted-foreground',
} as const;

export function StatsBlockRenderer({ block, editing }: StatsBlockRendererProps) {
  if ((block.data.variant ?? 'default') === 'oversized-numbers') {
    return <StatsOversizedNumbers block={block} editing={editing} />;
  }

  const {
    title,
    subtitle,
    items,
    columns = 3,
    style = 'cards',
    alignment = 'center',
    accentColor = 'primary',
    showDividers = false,
  } = block.data;

  const alignClass = ALIGN_CLASS[alignment];
  const accentClass = ACCENT_CLASS[accentColor];

  const renderItem = (item: StatItem, i: number) => (
    <div
      key={item.id}
      className={cn(
        'flex flex-col',
        alignClass,
        style === 'cards' && 'p-6 bg-muted/30 rounded-xl border border-border/50',
        style !== 'cards' && showDividers && i > 0 && 'border-l border-border pl-8',
      )}
      {...(editing ? { 'data-block-item': String(i) } : {})}
    >
      <div className="flex items-baseline gap-1">
        {item.prefix ? (
          <span className={cn('text-2xl md:text-3xl font-bold', accentClass)}>{item.prefix}</span>
        ) : null}
        <span
          className={cn(
            'inline-block min-w-[2rem] text-3xl md:text-4xl font-bold leading-none',
            accentClass,
            "empty:before:text-muted-foreground/50 empty:before:content-['0']",
          )}
          {...(editing ? { 'data-editable': `data.items[${i}].value` } : {})}
        >
          {item.value}
        </span>
        {item.suffix ? (
          <span className={cn('text-2xl md:text-3xl font-bold', accentClass)}>{item.suffix}</span>
        ) : null}
      </div>
      <div
        className={cn(
          'mt-2 inline-block min-w-[3rem] text-muted-foreground',
          style === 'minimal' && 'text-sm',
          "empty:before:text-muted-foreground/50 empty:before:content-['Label']",
        )}
        {...(editing ? { 'data-editable': `data.items[${i}].label` } : {})}
      >
        {item.label}
      </div>
      {(item.description || editing) && (
        <p
          className={cn(
            'mt-1 text-sm text-muted-foreground/80 inline-block min-w-[3rem]',
            "empty:before:text-muted-foreground/40 empty:before:content-['Description']",
          )}
          {...(editing ? { 'data-editable': `data.items[${i}].description` } : {})}
        >
          {item.description}
        </p>
      )}
    </div>
  );

  const heading = (
    <div className={cn('mb-8', alignClass)}>
      {(title || editing) && (
        <h2
          className="inline-block min-w-[6rem] text-2xl md:text-3xl font-bold text-foreground mb-2 empty:before:text-muted-foreground/50 empty:before:content-['Stats_title']"
          {...(editing ? { 'data-editable': 'data.title' } : {})}
        >
          {title}
        </h2>
      )}
      {(subtitle || editing) && (
        <p
          className="inline-block min-w-[6rem] text-muted-foreground empty:before:text-muted-foreground/50 empty:before:content-['Subtitle']"
          {...(editing ? { 'data-editable': 'data.subtitle' } : {})}
        >
          {subtitle}
        </p>
      )}
    </div>
  );

  if (style === 'inline') {
    return (
      <div className="my-12 py-8 border-y border-border">
        {heading}
        <div
          className={cn(
            'flex flex-wrap gap-8 md:gap-16',
            alignment === 'center' && 'justify-center',
            alignment === 'right' && 'justify-end',
            alignment === 'left' && 'justify-start',
          )}
        >
          {items.map(renderItem)}
        </div>
      </div>
    );
  }

  if (style === 'minimal') {
    return (
      <div className="my-12">
        {heading}
        <div className={cn('grid gap-8', GRID_CLASSES[columns])}>{items.map(renderItem)}</div>
      </div>
    );
  }

  // Cards
  return (
    <div className="my-12">
      {heading}
      <div className={cn('grid gap-6', GRID_CLASSES[columns])}>{items.map(renderItem)}</div>
    </div>
  );
}
